// Figma: 3357:20107 — see stories/blocks/datawrapper-embed.stories.tsx
"use client";

import { useEffect, useRef } from "react";

/**
 * Renders a live, responsive Datawrapper chart.
 *
 * Datawrapper serves each chart from `datawrapper.dwcdn.net/<id>/` and posts a
 * `datawrapper-height` message so the host can size the iframe to its content
 * (charts have no fixed aspect ratio). We mirror Datawrapper's canonical embed:
 * a full-width iframe (the `width: 0; min-width: 100%` trick) plus a message
 * listener that resizes only our own iframe, matched by `contentWindow`.
 *
 * A `<noscript>` PNG fallback keeps the chart visible without JavaScript.
 */
const DATAWRAPPER_ORIGIN = "https://datawrapper.dwcdn.net";

type DatawrapperEmbedProps = {
  chartId: string;
  title: string;
  /** Pin to a specific published version (e.g. for a historical article whose
   * numbers must stay frozen even if the chart is later republished).
   * Omit to always show the chart's current published version. */
  version?: number;
  /** Accepts a string too since MDX bodies can't use `{502}` expression syntax (blocked by next-mdx-remote's blockJS default). */
  minHeight?: number | string;
  fallbackImage?: string;
  fallbackAlt?: string;
};

export function DatawrapperEmbed({
  chartId,
  title,
  version,
  minHeight: minHeightProp = 400,
  fallbackImage,
  fallbackAlt,
}: DatawrapperEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const parsedMinHeight =
    typeof minHeightProp === "string" ? Number(minHeightProp) : minHeightProp;
  const minHeight = Number.isFinite(parsedMinHeight) ? parsedMinHeight : 400;

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== DATAWRAPPER_ORIGIN) return;

      const heights = (
        event.data as { "datawrapper-height"?: Record<string, number> }
      )?.["datawrapper-height"];
      const iframe = iframeRef.current;
      if (!heights || !iframe || event.source !== iframe.contentWindow) return;

      // Keyed by chart id (falling back to the first entry) so a page with
      // more than one Datawrapper embed can't resize the wrong iframe.
      const height = heights[chartId] ?? Object.values(heights)[0];
      if (typeof height === "number") iframe.style.height = `${height}px`;
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chartId]);

  return (
    <>
      <iframe
        ref={iframeRef}
        title={title}
        aria-label={title}
        src={`${DATAWRAPPER_ORIGIN}/${chartId}/${version != null ? `${version}/` : ""}`}
        scrolling="no"
        loading="lazy"
        height={minHeight}
        style={{ width: 0, minWidth: "100%", minHeight, border: "none" }}
        // eslint-disable-next-line react/iframe-missing-sandbox -- Datawrapper charts need scripts, same-origin (dwcdn.net) and popups for the interactive embed and its height postMessage.
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
      {fallbackImage && (
        <noscript>
          {/* eslint-disable-next-line @next/next/no-img-element -- static fallback for no-JS; next/image can't run without JS. */}
          <img
            src={fallbackImage}
            alt={fallbackAlt ?? title}
            style={{ width: "100%", height: "auto" }}
          />
        </noscript>
      )}
    </>
  );
}
